import React, { useContext, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { PackageService } from '../../../services/package-service';
import { ShippingServiceSearchableDropdown } from '../../../components/shipping-service-selector/shipping-service-searchable-dropdown';
import { ShippingServiceDropdownSelectedValue } from '../../../components/shipping-service-selector/shipping-service-dropdown-selected-value';
import { useServiceCollectionGroup } from '../../../components/shipping-service-selector/group-hooks/use-service-collection-group';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../hooks/use-has-rate-shopper-access-level';
import { useRateShopper } from '../../../pages/settings/rate-shopper/hooks/use-rate-shopper';
import { groupByCustomAndAddGenericPackage } from '../../../fulfillment-planner/package/dropdown/package-group-resolver';
import Global from '../../../store/global-selectors';

import { ProductDetailsContext } from '../context';
import { ServicesPackagesConfirmations } from '../service-package-confirmation.container';
import { ProductDetailsPackageDropdown } from './package-dropdown';
import { ProductDetailsConfirmationDropdown } from './confirmation-dropdown';
import { messages } from '../messages';

import style from '../style.module.css';

const EMPTY_PACKAGE_ID = '';
const EMPTY_CONFIRMATION_ID = '';

const getInheritedData = (contextValue, isInternational) => {
  const { currentProductProfile, parentProduct } = contextValue;
  if (isInternational) {
    const hasFixedServiceFromPreset = !!currentProductProfile.intlServiceId;
    const hasRateShopperFromPreset = !!currentProductProfile.intlRateShopperGuid;
    const inheritedRateShopperGuid = currentProductProfile.intlRateShopperGuid || parentProduct.intlRateShopperGuid;
    return {
      inheritedServiceId: currentProductProfile.intlServiceId || parentProduct.intlServiceId,
      isServiceInheritedFromPreset: hasFixedServiceFromPreset || hasRateShopperFromPreset,
      inheritedProviderId: currentProductProfile.intlProviderId || parentProduct.intlProviderId,
      inheritedPackageTypeId: currentProductProfile.intlPackageTypeId || parentProduct.intlPackageTypeId,
      isPackageInheritedFromPreset: !!currentProductProfile.intlPackageTypeId,
      inheritedConfirmationId: currentProductProfile.intlConfirmationId || parentProduct.intlConfirmationId,
      isConfirmationInheritedFromPreset: !!currentProductProfile.intlConfirmationId,
      inheritedRateShopperGuid,
      isRateShopperInheritedFromPreset: hasRateShopperFromPreset,
    };
  }
  const hasFixedServiceFromPreset = !!currentProductProfile.serviceId;
  const hasRateShopperFromPreset = !!currentProductProfile.domesticRateShopperGuid;
  const inheritedRateShopperGuid = currentProductProfile.domesticRateShopperGuid || parentProduct.domesticRateShopperGuid;
  return {
    inheritedServiceId: currentProductProfile.serviceId || parentProduct.serviceId,
    isServiceInheritedFromPreset: hasFixedServiceFromPreset || hasRateShopperFromPreset,
    inheritedProviderId: currentProductProfile.providerId || parentProduct.providerId,
    inheritedPackageTypeId: currentProductProfile.packageTypeId || parentProduct.packageTypeId,
    isPackageInheritedFromPreset: !!currentProductProfile.packageTypeId,
    inheritedConfirmationId: currentProductProfile.confirmationId || parentProduct.confirmationId,
    isConfirmationInheritedFromPreset: !!currentProductProfile.confirmationId,
    inheritedRateShopperGuid,
    isRateShopperInheritedFromPreset: hasRateShopperFromPreset,
  };
};

const getData = (contextValue, isInternational) => {
  return {
    serviceId: isInternational ? contextValue.internationalServiceId : contextValue.domesticServiceId,
    onServiceIdChanged: isInternational ? contextValue.setInternationalServiceId : contextValue.setDomesticServiceId,
    providerId: isInternational ? contextValue.internationalProviderId : contextValue.domesticProviderId,
    onProviderIdChanged: isInternational ? contextValue.setInternationalProviderId : contextValue.setDomesticProviderId,
    packageTypeId: isInternational ? contextValue.internationalPackageTypeId : contextValue.domesticPackageTypeId,
    onPackageTypeIdChanged: isInternational ? contextValue.setInternationalPackageTypeId : contextValue.setDomesticPackageTypeId,
    confirmationId: isInternational ? contextValue.internationalConfirmationId : contextValue.domesticConfirmationId,
    onConfirmationIdChanged: isInternational ? contextValue.setInternationalConfirmationId : contextValue.setDomesticConfirmationId,
    rateShopperGuid: isInternational ? contextValue.internationalRateShopperGuid : contextValue.domesticRateShopperGuid,
    onRateShopperGuidChanged: isInternational ? contextValue.setInternationalRateShopperGuid : contextValue.setDomesticRateShopperGuid,
    inheritedValues: getInheritedData(contextValue, isInternational),
  };
};

const renderInheritedInfo = (propertyName, isInheritedFromPreset) => {
  if (!propertyName) {
    return null;
  }
  if (isInheritedFromPreset) {
    return (
      <div className={style['profile-info']} title={propertyName}>
        <FormattedMessage {...messages.presetGroupInfo} />
        &nbsp;{propertyName}
      </div>
    );
  }
  return (
    <div className={style['profile-info']} title={propertyName}>
      <FormattedMessage {...messages.parentInfo} />
      &nbsp;{propertyName}
    </div>
  );
};

export const ProductDetailsEnabledCascadingDropdowns = ({ isInternational = false }) => {
  const contextValue = useContext(ProductDetailsContext);
  const { userHasPermission } = contextValue;
  const {
    serviceId,
    onServiceIdChanged,
    providerId,
    onProviderIdChanged,
    packageTypeId,
    onPackageTypeIdChanged,
    confirmationId,
    onConfirmationIdChanged,
    rateShopperGuid,
    onRateShopperGuidChanged,
    inheritedValues,
  } = getData(contextValue, isInternational);

  const serviceCollections = useSelector(Global.selectSellerServiceCollections);
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const { prePopulatePackage } = useRateShopper();

  const [isSecondDropdownEnabled, setIsSecondDropdownEnabled] = useState(!!serviceId || (!!rateShopperGuid && !prePopulatePackage));
  const [isThirdDropdownEnabled, setIsThirdDropdownEnabled] = useState(!!serviceId);

  // Build selected option for the dropdown
  const selectedServiceItem = useMemo(() => {
    if (rateShopperGuid) {
      return {
        serviceCollectionGuid: rateShopperGuid,
        serviceId: undefined,
        sellerLabelProviderId: undefined,
      };
    }
    if (serviceId) {
      return {
        serviceId,
        sellerLabelProviderId: providerId,
        serviceCollectionGuid: undefined,
      };
    }
    return {};
  }, [serviceId, providerId, rateShopperGuid]);

  // Get selected service collection to check if it's a template
  const selectedServiceCollection = serviceCollections.find((collection) => collection.serviceCollectionGuid === rateShopperGuid);
  const isTemplate = selectedServiceCollection?.isTemplate;

  // Use the service collection group hook
  const serviceCollectionGroup = useServiceCollectionGroup(selectedServiceItem, true);

  return (
    <ServicesPackagesConfirmations
      confirmationId={confirmationId}
      inheritedConfirmationId={inheritedValues.inheritedConfirmationId}
      inheritedPackageTypeId={inheritedValues.inheritedPackageTypeId}
      inheritedProviderId={inheritedValues.inheritedProviderId}
      inheritedServiceId={inheritedValues.inheritedServiceId}
      packageTypeId={packageTypeId}
      serviceId={serviceId}
    >
      {({
        serviceGroups,
        confirmations,
        selectedConfirmationName,
        selectedServiceName,
        selectedPackageName,
        packages,
        packageGroups,
        inheritedServiceName,
        inheritedPackageName,
        inheritedConfirmationName,
      }) => {
        // Transform service groups to match ShippingServiceSearchableDropdown format
        const transformedServiceGroups = serviceGroups.map((serviceGroup) => ({
          name: serviceGroup.serviceGroupTypeObject?.name ?? serviceGroup.name,
          groupId: serviceGroup.serviceGroupTypeObject?.sellerLabelProviderId ?? serviceGroup.name,
          items: serviceGroup.services.map((service) => ({
            name: service.name,
            serviceId: service.serviceId,
            sellerLabelProviderId: service.labelProviderId,
          })),
        }));

        // Add rate shopper group at the top if available
        if (showRateShopper && serviceCollectionGroup?.items?.length) {
          transformedServiceGroups.unshift(serviceCollectionGroup);
        }

        const selectedOption = {
          ...selectedServiceItem,
          isTemplate,
          name: rateShopperGuid ? selectedServiceCollection?.name : selectedServiceName,
        };

        const renderSelectedValue = ({
          serviceId: selectedSvcId,
          sellerLabelProviderId,
          serviceCollectionGuid,
          isTemplate: isTemplateValue,
        }) => {
          if (!serviceCollectionGuid && !selectedSvcId) {
            return;
          }

          return (
            <ShippingServiceDropdownSelectedValue
              isTemplate={isTemplateValue}
              sellerLabelProviderId={sellerLabelProviderId}
              serviceCollectionGuid={serviceCollectionGuid}
              serviceId={selectedSvcId}
            />
          );
        };

        // When rate shopper is selected, filter package groups to only show generic/custom packages
        const displayPackageGroups = rateShopperGuid ? groupByCustomAndAddGenericPackage(packages) : packageGroups;

        // Get inherited rate shopper name if applicable
        const inheritedRateShopperCollection = inheritedValues.inheritedRateShopperGuid
          ? serviceCollections.find((collection) => collection.serviceCollectionGuid === inheritedValues.inheritedRateShopperGuid)
          : null;
        const displayInheritedServiceName = inheritedRateShopperCollection?.name || inheritedServiceName;

        const onServiceChanged = (selectedService) => {
          // Handle clearing the selection
          if (!selectedService || (!selectedService.serviceId && !selectedService.serviceCollectionGuid)) {
            setIsSecondDropdownEnabled(false);
            setIsThirdDropdownEnabled(false);
            onServiceIdChanged('');
            onProviderIdChanged('');
            onRateShopperGuidChanged('');
            onPackageTypeIdChanged(EMPTY_PACKAGE_ID);
            onConfirmationIdChanged(EMPTY_CONFIRMATION_ID);
            return;
          }

          // Handle rate shopper selection
          if (selectedService.serviceCollectionGuid) {
            setIsSecondDropdownEnabled(!prePopulatePackage);
            setIsThirdDropdownEnabled(false);
            onRateShopperGuidChanged(selectedService.serviceCollectionGuid);
            onServiceIdChanged('');
            onProviderIdChanged('');
            // Clear package and confirmation when switching to rate shopper
            onPackageTypeIdChanged(EMPTY_PACKAGE_ID);
            onConfirmationIdChanged(EMPTY_CONFIRMATION_ID);
            return;
          }

          // Handle fixed service selection
          setIsSecondDropdownEnabled(true);
          setIsThirdDropdownEnabled(true);
          onServiceIdChanged(selectedService.serviceId);
          onProviderIdChanged(selectedService.sellerLabelProviderId);
          onRateShopperGuidChanged('');

          // Find the carrier ID for the selected service to check package compatibility
          const selectedServiceData = serviceGroups
            .flatMap((group) => group.services)
            .find((service) => service.serviceId === selectedService.serviceId);

          if (!packageTypeId) {
            onPackageTypeIdChanged(PackageService.getStandardPackageId().toString());
            return;
          }

          if (selectedServiceData) {
            const packageIsSupported = !!packages.find((pack) => pack.carrierId === selectedServiceData.carrierId);
            if (!packageIsSupported) {
              onPackageTypeIdChanged(PackageService.getStandardPackageId().toString());
            }
          }
        };

        return (
          <>
            <div className={style['options-dropdown-wrapper']}>
              <ShippingServiceSearchableDropdown
                id={isInternational ? 'international-service' : 'domestic-service'}
                isDisabled={!userHasPermission}
                onSelected={onServiceChanged}
                placeholderMessageDescriptor={isInternational ? messages.defaultInternationalServiceName : messages.defaultServiceName}
                renderSelectedValue={renderSelectedValue}
                selectedOption={selectedOption}
                serviceGroups={transformedServiceGroups}
                shouldTruncateGroupHeader={false}
              />
              {renderInheritedInfo(displayInheritedServiceName, inheritedValues.isServiceInheritedFromPreset)}
            </div>
            <div className={style['options-dropdown-wrapper']}>
              <ProductDetailsPackageDropdown
                isEnabled={isSecondDropdownEnabled}
                isInternational={isInternational}
                onPackageChanged={onPackageTypeIdChanged}
                packageGroups={displayPackageGroups}
                selectedPackageName={selectedPackageName}
              />
              {renderInheritedInfo(inheritedPackageName, inheritedValues.isPackageInheritedFromPreset)}
            </div>
            <div className={style['options-dropdown-wrapper']}>
              <ProductDetailsConfirmationDropdown
                confirmations={confirmations}
                isEnabled={isThirdDropdownEnabled}
                isInternational={isInternational}
                onConfirmationChanged={onConfirmationIdChanged}
                selectedConfirmationName={selectedConfirmationName}
              />
              {renderInheritedInfo(inheritedConfirmationName, inheritedValues.isConfirmationInheritedFromPreset)}
            </div>
          </>
        );
      }}
    </ServicesPackagesConfirmations>
  );
};

ProductDetailsEnabledCascadingDropdowns.propTypes = {
  isInternational: PropTypes.bool,
};
